import { TimelineSegment } from '../ui/Timeline';

export interface TimelineActivityInput {
  id: number;
  typeOfWorkName?: string;
  timeStart?: string;
  timeEnd?: string;
  hours?: number;
  allocatedHours?: number;
  sortOrder?: number;
}

/**
 * Строит сегменты шкалы активности для смены.
 * Для открытых смен последний сегмент всегда тянется до sessionEnd (обычно «сейчас»),
 * иначе после редактирования в упрощённом режиме полоса выглядит обрезанной.
 */
export function buildSessionTimelineSegments(
  activities: TimelineActivityInput[],
  sessionTimeStart: string,
  sessionTimeEnd?: string
): TimelineSegment[] {
  if (activities.length === 0) return [];

  const sessionStart = new Date(sessionTimeStart);
  const sessionEnd = sessionTimeEnd ? new Date(sessionTimeEnd) : new Date();
  const isOpenSession = !sessionTimeEnd;
  const sessionDurationMs = Math.max(1, sessionEnd.getTime() - sessionStart.getTime());

  const sortedActivities = [...activities].sort((a, b) => {
    const oA = a.sortOrder ?? 999999;
    const oB = b.sortOrder ?? 999999;
    if (oA !== oB) return oA - oB;
    const tA = a.timeStart ? new Date(a.timeStart).getTime() : 0;
    const tB = b.timeStart ? new Date(b.timeStart).getTime() : 0;
    return tA - tB;
  });

  const totalAllocatedHours = sortedActivities.reduce(
    (sum, a) => sum + (a.hours ?? a.allocatedHours ?? 0),
    0
  );

  let cumulativeMs = sessionStart.getTime();

  return sortedActivities.map((activity, index) => {
    const hours = activity.hours ?? activity.allocatedHours ?? 0;
    const isLast = index === sortedActivities.length - 1;
    let start: Date;
    let end: Date;

    if (activity.timeStart) {
      start = new Date(activity.timeStart);
      if (activity.timeEnd) {
        end = new Date(activity.timeEnd);
        if (isOpenSession && isLast && end.getTime() < sessionEnd.getTime()) {
          end = sessionEnd;
        }
      } else if (!isLast && sortedActivities[index + 1].timeStart) {
        end = new Date(sortedActivities[index + 1].timeStart!);
      } else {
        end = sessionEnd;
      }
      cumulativeMs = end.getTime();
    } else {
      start = new Date(cumulativeMs);
      if (isOpenSession) {
        const shareMs =
          totalAllocatedHours > 0
            ? (hours / totalAllocatedHours) * sessionDurationMs
            : sessionDurationMs / sortedActivities.length;
        end = isLast ? sessionEnd : new Date(cumulativeMs + shareMs);
      } else {
        cumulativeMs += hours * 60 * 60 * 1000;
        end = new Date(cumulativeMs);
      }
      cumulativeMs = end.getTime();
    }

    const durationHours = hours || (end.getTime() - start.getTime()) / (1000 * 60 * 60);

    return {
      id: activity.id,
      start,
      end,
      label: activity.typeOfWorkName || 'Не выбрано',
      duration: durationHours,
      data: activity
    };
  });
}
